import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getInstagramReel } from '@domain/features/instagram-reels/instagram-reels-feature'
import { getNewsActivitiesByIds } from '@domain/features/news/news-feature'
import { getPodcastEpisode } from '@domain/features/podcasts/podcasts-feature'
import {
  INSTAGRAM_REEL_FAVOURITE_TYPE,
  NEWS_FAVOURITE_TYPE,
  observeFavouriteItems,
  PODCAST_FAVOURITE_TYPE,
  toggleFavouriteItem,
  WEBINAR_FAVOURITE_TYPE,
} from '@domain/features/user-favourites/user-favourites-feature'
import { getWebinar } from '@domain/features/webinars/webinars-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'
import type { InstagramReel } from '@domain/models/instagram-reel'
import type { PodcastEpisode } from '@domain/models/podcast'
import type { UserFavourite, UserFavouriteType } from '@domain/models/user-favourite'
import type { Webinar } from '@domain/models/webinar'
import { useAuthUser } from '@presentation/hooks/useAuthUser'

export type HydratedUserFavouriteItem =
  | {
    type: typeof NEWS_FAVOURITE_TYPE
    favourite: UserFavourite
    activity: ActivityExplore
  }
  | {
    type: typeof PODCAST_FAVOURITE_TYPE
    favourite: UserFavourite
    episode: PodcastEpisode
  }
  | {
    type: typeof WEBINAR_FAVOURITE_TYPE
    favourite: UserFavourite
    webinar: Webinar
  }
  | {
    type: typeof INSTAGRAM_REEL_FAVOURITE_TYPE
    favourite: UserFavourite
    reel: InstagramReel
  }

function uniqueItemIdsByType(
  favourites: UserFavourite[],
  type: UserFavouriteType,
): string[] {
  return [...new Set(
    favourites
      .filter((item) => item.type === type)
      .map((item) => item.itemId)
      .filter((itemId) => itemId.trim().length > 0),
  )]
}

async function fetchItemsById<T>(
  ids: string[],
  fetcher: (id: string) => Promise<T>,
): Promise<{ items: Map<string, T>, hadFailures: boolean }> {
  if (ids.length === 0) {
    return { items: new Map<string, T>(), hadFailures: false }
  }

  const settled = await Promise.allSettled(
    ids.map(async (id) => ({ id, value: await fetcher(id) })),
  )
  const items = new Map<string, T>()
  let hadFailures = false

  settled.forEach((result) => {
    if (result.status === 'fulfilled') {
      items.set(result.value.id, result.value.value)
      return
    }
    hadFailures = true
  })

  return { items, hadFailures }
}

async function hydrateUserFavouriteItems(
  favourites: UserFavourite[],
): Promise<{ items: HydratedUserFavouriteItem[], hadFailures: boolean }> {
  const newsIds = uniqueItemIdsByType(favourites, NEWS_FAVOURITE_TYPE)
  const podcastIds = uniqueItemIdsByType(favourites, PODCAST_FAVOURITE_TYPE)
  const webinarIds = uniqueItemIdsByType(favourites, WEBINAR_FAVOURITE_TYPE)
  const reelIds = uniqueItemIdsByType(favourites, INSTAGRAM_REEL_FAVOURITE_TYPE)

  let hadFailures = false
  let newsItems = new Map<string, ActivityExplore>()

  if (newsIds.length > 0) {
    try {
      const activities = await getNewsActivitiesByIds(newsIds)
      newsItems = new Map(
        activities.map((activity) => [activity.uid, activity]),
      )
    } catch {
      hadFailures = true
    }
  }

  const [
    { items: podcastItems, hadFailures: podcastFailures },
    { items: webinarItems, hadFailures: webinarFailures },
    { items: reelItems, hadFailures: reelFailures },
  ] = await Promise.all([
    fetchItemsById(podcastIds, getPodcastEpisode),
    fetchItemsById(webinarIds, getWebinar),
    fetchItemsById(reelIds, getInstagramReel),
  ])

  hadFailures = hadFailures || podcastFailures || webinarFailures || reelFailures

  const hydratedItems: HydratedUserFavouriteItem[] = []
  favourites.forEach((favourite) => {
    if (favourite.type === NEWS_FAVOURITE_TYPE) {
      const activity = newsItems.get(favourite.itemId)
      if (activity) {
        hydratedItems.push({
          type: NEWS_FAVOURITE_TYPE,
          favourite,
          activity,
        })
      }
      return
    }

    if (favourite.type === PODCAST_FAVOURITE_TYPE) {
      const episode = podcastItems.get(favourite.itemId)
      if (episode) {
        hydratedItems.push({
          type: PODCAST_FAVOURITE_TYPE,
          favourite,
          episode,
        })
      }
      return
    }

    if (favourite.type === WEBINAR_FAVOURITE_TYPE) {
      const webinar = webinarItems.get(favourite.itemId)
      if (webinar) {
        hydratedItems.push({
          type: WEBINAR_FAVOURITE_TYPE,
          favourite,
          webinar,
        })
      }
      return
    }

    if (favourite.type === INSTAGRAM_REEL_FAVOURITE_TYPE) {
      const reel = reelItems.get(favourite.itemId)
      if (reel) {
        hydratedItems.push({
          type: INSTAGRAM_REEL_FAVOURITE_TYPE,
          favourite,
          reel,
        })
      }
    }
  })

  return { items: hydratedItems, hadFailures }
}

export function useUserFavourites() {
  const { isAuthenticated, loading: authLoading } = useAuthUser()
  const [items, setItems] = useState<HydratedUserFavouriteItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingKeys, setTogglingKeys] = useState<string[]>([])
  const hydrationVersion = useRef(0)

  useEffect(() => {
    setError(null)

    if (authLoading) {
      setLoading(true)
      return
    }

    if (!isAuthenticated) {
      setItems([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    const hydrate = async (favourites: UserFavourite[]) => {
      const currentVersion = ++hydrationVersion.current

      try {
        const { items: hydratedItems, hadFailures } = await hydrateUserFavouriteItems(favourites)
        if (cancelled || currentVersion !== hydrationVersion.current) {
          return
        }

        setItems(hydratedItems)
        setError(hadFailures ? 'Some favourites could not be loaded.' : null)
        setLoading(false)
      } catch (nextError) {
        if (cancelled || currentVersion !== hydrationVersion.current) {
          return
        }
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to load favourite items',
        )
        setItems([])
        setLoading(false)
      }
    }

    const unsubscribe = observeFavouriteItems(
      (favourites) => {
        void hydrate(favourites)
      },
      (nextError) => {
        if (cancelled) {
          return
        }
        setError(nextError.message)
        setLoading(false)
      },
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [authLoading, isAuthenticated])

  const toggleFavourite = useCallback(async (
    itemId: string,
    type: UserFavouriteType,
    onAuthRequired?: () => void,
  ) => {
    if (!isAuthenticated) {
      onAuthRequired?.()
      return
    }

    const key = `${type}:${itemId}`
    setTogglingKeys((prev) => prev.includes(key) ? prev : [...prev, key])

    try {
      setError(null)
      await toggleFavouriteItem(itemId, type)
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : 'Failed to update favourite item',
      )
    } finally {
      setTogglingKeys((prev) => prev.filter((existingKey) => existingKey !== key))
    }
  }, [isAuthenticated])

  const isToggling = useCallback((itemId: string, type: UserFavouriteType) => (
    togglingKeys.includes(`${type}:${itemId}`)
  ), [togglingKeys])

  const itemsByType = useMemo(() => ({
    news: items.filter((item) => item.type === NEWS_FAVOURITE_TYPE),
    podcasts: items.filter((item) => item.type === PODCAST_FAVOURITE_TYPE),
    webinars: items.filter((item) => item.type === WEBINAR_FAVOURITE_TYPE),
    instagramReels: items.filter((item) => item.type === INSTAGRAM_REEL_FAVOURITE_TYPE),
  }), [items])

  return {
    items,
    itemsByType,
    loading: loading || authLoading,
    error,
    isAuthenticated,
    toggleFavourite,
    isToggling,
  }
}
